import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Legend } from "recharts";
import { Moon, TrendingUp, Clock, BarChart3 } from "lucide-react";

const SleepCharts = ({ entries }) => {
  // Prepare chart data - reverse to show chronological order
  const chartData = [...entries].reverse().map(entry => ({
    date: entry.date.split('-').slice(1).join('/'),
    efficiency: entry.sleep_efficiency,
    sleepTime: Math.round(entry.total_sleep_time_minutes / 60 * 10) / 10,
    timeInBed: Math.round(entry.total_time_in_bed_minutes / 60 * 10) / 10,
    quality: entry.quality === "Very good" ? 5 : 
             entry.quality === "Good" ? 4 : 
             entry.quality === "Fair" ? 3 : 
             entry.quality === "Poor" ? 2 : 1
  }));

  // Calculate averages
  const avgEfficiency = entries.length > 0 
    ? (entries.reduce((sum, e) => sum + e.sleep_efficiency, 0) / entries.length).toFixed(1)
    : 0;
  
  const avgSleepTime = entries.length > 0
    ? (entries.reduce((sum, e) => sum + e.total_sleep_time_minutes, 0) / entries.length / 60).toFixed(1)
    : 0;

  const avgTimeInBed = entries.length > 0
    ? (entries.reduce((sum, e) => sum + e.total_time_in_bed_minutes, 0) / entries.length / 60).toFixed(1)
    : 0;

  if (entries.length === 0) {
    return (
      <Card className="card-base text-center py-12">
        <BarChart3 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Not enough data for trends. Add more entries to see your sleep patterns.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="card-metric">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Avg Efficiency</p>
            <p className="text-2xl font-bold text-primary">{avgEfficiency}%</p>
          </div>
        </Card>
        
        <Card className="card-metric">
          <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
            <Moon className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Avg Sleep Time</p>
            <p className="text-2xl font-bold text-green-600">{avgSleepTime}h</p>
          </div>
        </Card>
        
        <Card className="card-metric">
          <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
            <Clock className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-slate-500">Avg Time in Bed</p>
            <p className="text-2xl font-bold text-amber-600">{avgTimeInBed}h</p>
          </div>
        </Card>
      </div>

      {/* Sleep Efficiency Chart */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Sleep Efficiency Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="efficiencyGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
              <YAxis domain={[0, 100]} stroke="#64748b" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value) => [`${value}%`, 'Efficiency']}
              />
              <Area 
                type="monotone" 
                dataKey="efficiency" 
                stroke="#2563EB" 
                strokeWidth={2}
                fill="url(#efficiencyGradient)"
              />
              {/* Reference lines for classification */}
              <Line 
                type="monotone" 
                dataKey={() => 85} 
                stroke="#22c55e" 
                strokeDasharray="5 5"
                strokeWidth={1}
                dot={false}
                name="Good (85%)"
              />
              <Line 
                type="monotone" 
                dataKey={() => 70} 
                stroke="#f59e0b" 
                strokeDasharray="5 5"
                strokeWidth={1}
                dot={false}
                name="Moderate (70%)"
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-green-500"></div>
              <span className="text-slate-600">Good (≥85%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-amber-500"></div>
              <span className="text-slate-600">Moderate (70-84%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-500"></div>
              <span className="text-slate-600">Poor (&lt;70%)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sleep Duration Chart */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Moon className="h-5 w-5 text-green-600" />
            Sleep Duration Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="sleepGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="bedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
              <YAxis stroke="#64748b" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value) => [`${value} hours`]}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="timeInBed" 
                name="Time in Bed"
                stroke="#f59e0b" 
                strokeWidth={2}
                fill="url(#bedGradient)"
              />
              <Area 
                type="monotone" 
                dataKey="sleepTime" 
                name="Actual Sleep"
                stroke="#22c55e" 
                strokeWidth={2}
                fill="url(#sleepGradient)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Quality Rating Chart */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-slate-600" />
            Self-Reported Sleep Quality
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
              <YAxis 
                domain={[1, 5]} 
                ticks={[1, 2, 3, 4, 5]}
                tickFormatter={(value) => {
                  const labels = { 1: 'V.Poor', 2: 'Poor', 3: 'Fair', 4: 'Good', 5: 'V.Good' };
                  return labels[value] || value;
                }}
                stroke="#64748b" 
                fontSize={11}
                width={50}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'white', 
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value) => {
                  const labels = { 1: 'Very Poor', 2: 'Poor', 3: 'Fair', 4: 'Good', 5: 'Very Good' };
                  return [labels[value], 'Quality'];
                }}
              />
              <Line 
                type="monotone" 
                dataKey="quality" 
                stroke="#6366f1" 
                strokeWidth={2}
                dot={{ fill: '#6366f1', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default SleepCharts;
